/**
 * The reception desk's arithmetic: which of today's tickets a search or a filter
 * means, the list the desk reads, and how the server's wait estimate is said. Kept
 * apart from the page so a test can run it.
 */
import { pastTarget } from './callingWait'
import { deskQueueIds } from './floorBoard'
import type { CounterView, QueueView, Ticket, TicketState, VisitField, WorkProfile } from './types'
import { waitText, type Estimate } from './estimate'
import type { StaffKey, Vars } from './staffText'

/** The views of the day's list. Done is served and cancelled; missed stands apart.
 *  A set-aside (parked) ticket is waiting to be taken back, so it counts as waiting.
 *  Over is the waiting tickets at or past their target: a subset of waiting, not a home. */
export type DayFilter = 'all' | 'waiting' | 'desk' | 'done' | 'missed' | 'over'
export const DAY_FILTERS: readonly DayFilter[] = ['all', 'waiting', 'desk', 'done', 'missed', 'over']

const WAITING: readonly TicketState[] = ['waiting', 'parked']
const AT_A_DESK: readonly TicketState[] = ['called', 'serving', 'wrapup']
const ENDED: readonly TicketState[] = ['closed', 'void']

export type SlipPrint = 'printing' | 'printed' | 'failed' | 'kept'

/** The slip's print line. None once the ticket stopped waiting: "Say the number" is
 *  moot for a cancelled or finished ticket. */
export function slipPrintLine(print: SlipPrint, again: boolean, waiting: boolean): 'printing' | 'failed' | 'again' | 'printed' | null {
  if (!waiting || print === 'kept') return null
  if (print === 'printing' || print === 'failed') return print
  return again ? 'again' : 'printed'
}

/** A waiting ticket at or past its queue's target, as the server's breaching counts it. */
export function overTarget(ticket: Ticket, now: number = Date.now()): boolean {
  return ticket.state === 'waiting' && pastTarget([ticket], now) > 0
}

export function inFilter(ticket: Ticket, filter: DayFilter, now: number = Date.now()): boolean {
  switch (filter) {
    case 'all':
      return true
    case 'waiting':
      return WAITING.includes(ticket.state)
    case 'desk':
      return AT_A_DESK.includes(ticket.state)
    case 'done':
      return ENDED.includes(ticket.state)
    case 'missed':
      return ticket.state === 'no_show'
    case 'over':
      return overTarget(ticket, now)
  }
}

/** "A-042" drawn as its letter and its digits: the letter goes on the stub. */
export function numberParts(ticket: Pick<Ticket, 'displayNumber' | 'prefix'>): { letter: string; digits: string } {
  const at = ticket.displayNumber.lastIndexOf('-')
  if (at < 0) return { letter: '', digits: ticket.displayNumber }
  return { letter: ticket.displayNumber.slice(0, at) || ticket.prefix, digits: ticket.displayNumber.slice(at + 1) }
}

// The letters an Azerbaijani name has and a Latin keyboard may not. ə is left out
// of the fold on purpose: see nameMatches.
const FOLD: Record<string, string> = { ü: 'u', ö: 'o', ı: 'i', ş: 's', ç: 'c', ğ: 'g' }

export function fold(s: string): string {
  return s
    .toLocaleLowerCase('az')
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .replace(/[üöışçğ]/g, (c) => FOLD[c] ?? c)
}

const escape = (s: string) => s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')

/**
 * "Gunel" finds "Günel"; "Mammadova" and "Memmedova" both find "Məmmədova".
 *
 * ə is typed as a or as e depending on who is typing, so an a or an e in the query
 * also matches ə. An a does not match an e: "Ali" must not find "Eli".
 */
export function nameMatches(value: string, query: string): boolean {
  const q = fold(query.trim())
  if (!q) return true
  const pattern = [...q]
    .map((c) => (c === 'a' ? '[aə]' : c === 'e' ? '[eə]' : c === 'ə' ? '[əae]' : escape(c)))
    .join('')
  return new RegExp(pattern).test(fold(value))
}

/**
 * Whether a ticket is what the greeter typed: "A43", "a 043", "A-043" and "43"
 * all find A-043; a '+' or seven digits is a mobile, compared digit by digit;
 * anything else is looked for in the visit's name and the values it carries.
 */
export function ticketMatches(
  ticket: Pick<Ticket, 'displayNumber' | 'prefix' | 'sequence' | 'fields' | 'customerName' | 'customerPhone'>,
  raw: string,
): boolean {
  const q = raw.trim()
  if (!q) return true
  const digits = q.replace(/\D/g, '')
  if (/^[+\d\s().-]+$/.test(q) && (q.includes('+') || digits.length >= 7)) {
    // A local "050 123 45 67" finds +994501234567: the stored number has no leading 0.
    const local = digits.replace(/^0+/, '')
    return [ticket.customerPhone ?? '', ...Object.values(ticket.fields ?? {})].some((v) => {
      const d = v.replace(/\D/g, '')
      return digits !== '' && d !== '' && (d.includes(digits) || (local !== '' && d.includes(local)))
    })
  }
  const number = /^([^\d\s-]+)?[\s-]*0*(\d+)$/.exec(q)
  if (number) {
    if (ticket.sequence !== Number(number[2])) return false
    return !number[1] || numberParts(ticket).letter.toUpperCase() === number[1].toUpperCase()
  }
  return [ticket.customerName ?? '', ...Object.values(ticket.fields ?? {})].some((v) => v && nameMatches(v, q))
}

/** "+994 50 ••• •• 67": enough to recognise, not enough to read off a screen. */
export function maskPhone(raw: string): string {
  let seen = 0
  const digits = raw.replace(/\D/g, '').length
  return raw.replace(/\d/g, (d) => {
    seen++
    return seen <= 5 || seen > digits - 2 ? d : '•'
  })
}

/** "+994501234567" as "+994 50 123 45 67"; a number already spaced is kept as written. */
export function groupPhone(raw: string): string {
  const v = raw.trim()
  const digits = v.replace(/\D/g, '')
  if (/[\s().-]/.test(v) || digits.length < 8) return v
  const head = digits.slice(0, -7)
  const tail = digits.slice(-7)
  const country = head.length > 3 ? `${head.slice(0, 3)} ${head.slice(3)}` : head
  return `${v.startsWith('+') ? '+' : ''}${country} ${tail.slice(0, 3)} ${tail.slice(3, 5)} ${tail.slice(5)}`
}

export interface RowCustomer {
  name: string
  phone: string
  from: 'visit' | 'values' | 'booked' | ''
}

/**
 * Who a row of the day names, phone grouped and masked. Name and phone are one
 * source's pair: the visit a desk named, else the values given at issue, else a
 * booked customer's own name. Never one source's name with another's phone.
 */
export function rowCustomer(
  t: Pick<Ticket, 'fields' | 'customerName' | 'customerPhone'>,
  offered: readonly Pick<VisitField, 'key' | 'type'>[],
  bookedName = '',
): RowCustomer {
  const masked = (v: string) => (v ? maskPhone(groupPhone(v)) : '')
  const visitName = t.customerName?.trim() ?? ''
  const visitPhone = t.customerPhone?.trim() ?? ''
  if (visitName || visitPhone) return { name: visitName, phone: masked(visitPhone), from: 'visit' }
  let name = ''
  let phone = ''
  for (const f of offered) {
    const v = t.fields?.[f.key]?.trim()
    if (!v) continue
    if (f.type === 'phone') phone ||= masked(v)
    else if (f.type === 'text') name ||= v
  }
  if (name || phone) return { name, phone, from: 'values' }
  return { name: bookedName, phone: '', from: bookedName ? 'booked' : '' }
}

/**
 * The day the desk reads: every ticket of today, with the live copy of each one
 * still open laid over it. The live channel carries open tickets only, and open
 * tickets of an earlier day nobody ended are in it too; they belong in the list
 * because they are still in the hall.
 */
export function mergeDay(day: readonly Ticket[], open: readonly Ticket[]): Ticket[] {
  const byId = new Map<string, Ticket>()
  for (const t of day) byId.set(t.id, t)
  for (const t of open) byId.set(t.id, t)
  return [...byId.values()]
}

/**
 * The values this desk saved, laid over the merged day. A save of values runs no
 * engine command, so the live copy keeps the old ones until the ticket next moves.
 */
export function withSavedFields(tickets: readonly Ticket[], saved: Readonly<Record<string, Ticket['fields']>>): Ticket[] {
  return tickets.map((t) => (t.id in saved ? { ...t, fields: saved[t.id] } : t))
}

/**
 * Reads the day again soon after an open ticket leaves the live list (it just ended).
 * A delta arriving meanwhile neither cancels nor repeats a read already due: in a busy
 * branch deltas come faster than the delay, and a cancelled read waits for the minute poll.
 */
export function leaveWatcher(reload: () => void, delayMs = 1200) {
  let open = new Set<string>()
  let timer: ReturnType<typeof setTimeout> | undefined
  return {
    seen(ids: Iterable<string>) {
      const next = new Set(ids)
      const gone = [...open].some((id) => !next.has(id))
      open = next
      if (!gone || timer !== undefined) return
      timer = setTimeout(() => {
        timer = undefined
        reload()
      }, delayMs)
    },
    stop() {
      if (timer !== undefined) clearTimeout(timer)
      timer = undefined
    },
  }
}

/** Newest first: the eye goes to what was just handed over. */
export function newestFirst(a: Ticket, b: Ticket): number {
  return Date.parse(b.issuedAt) - Date.parse(a.issuedAt) || b.displayNumber.localeCompare(a.displayNumber)
}

/** A desk as reception reads it at a glance. */
export type DeskAvailability = 'free' | 'serving' | 'break' | 'closed'

/** Read off the server's derived status where sent, the stored state where not. */
export function deskAvailability(desk: Pick<CounterView, 'state' | 'status' | 'ticketId'>): DeskAvailability {
  if (desk.state === 'closed' || desk.status === 'closed') return 'closed'
  if (desk.status === 'serving' || desk.ticketId) return 'serving'
  if (desk.state === 'paused' || (desk.status && desk.status !== 'ready')) return 'break'
  return 'free'
}

const AVAILABILITY_ORDER: Record<DeskAvailability, number> = { free: 0, serving: 1, break: 2, closed: 3 }

/** Free desks first, closed last; the branch's own order within each. */
export function byAvailability<T extends Pick<CounterView, 'state' | 'status' | 'ticketId' | 'sortOrder'>>(desks: readonly T[]): T[] {
  return [...desks].sort(
    (a, b) => AVAILABILITY_ORDER[deskAvailability(a)] - AVAILABILITY_ORDER[deskAvailability(b)] || a.sortOrder - b.sortOrder,
  )
}

/** The letter a queue's tickets carry. */
export function queueLetter(queue: Pick<QueueView, 'letter' | 'code'>): string {
  return queue.letter || queue.code.slice(0, 1)
}

/** How many letters a desk row shows before "+N". */
export const DESK_LETTERS_SHOWN = 6

export interface DeskLetter<Q> {
  letter: string
  queues: Q[]
}

/** The ticket letters an open desk calls, one per letter, sorted; none for a closed desk. */
export function deskLetters<Q extends Pick<QueueView, 'queueId' | 'letter' | 'code'>>(
  desk: Pick<CounterView, 'state' | 'status' | 'ticketId' | 'workProfileId'>,
  queues: readonly Q[],
  profiles: readonly Pick<WorkProfile, 'id' | 'queueIds'>[] | null,
): { shown: DeskLetter<Q>[]; more: number } {
  if (deskAvailability(desk) === 'closed') return { shown: [], more: 0 }
  const byLetter = new Map<string, Q[]>()
  for (const id of deskQueueIds(desk, profiles ?? [])) {
    const queue = queues.find((q) => q.queueId === id)
    const letter = queue && queueLetter(queue)
    if (!queue || !letter) continue
    byLetter.set(letter, [...(byLetter.get(letter) ?? []), queue])
  }
  const all = [...byLetter].map(([letter, qs]) => ({ letter, queues: qs })).sort((a, b) => a.letter.localeCompare(b.letter))
  return { shown: all.slice(0, DESK_LETTERS_SHOWN), more: Math.max(0, all.length - DESK_LETTERS_SHOWN) }
}

/** The server's estimate as Reception says it: '~7 min', '~15–20 min', '2 h+', '—' with no desk. */
export function estimateLabel(e: Estimate | null | undefined, say: (key: StaffKey, vars?: Vars) => string): string {
  return waitText(e, {
    min: (m) => say('estimate.min', { m }),
    range: (low, high) => say('estimate.range', { low, high }),
    over: say('estimate.over'),
  })
}
